import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { msalInstance, loginRequest, securityGroups, derivePermissions } from './authConfig';
import authenticatedDataverseService from './services/authenticatedDataverseService';
import Calendar from './components/Calendar';
import Administration from './components/Administration';
import ScheduleGenerator from './components/ScheduleGenerator';
import RegistrationReport from './components/RegistrationReport';
import './App.css';

const APP_VERSION = '0.1.7';

// Main App Content Component
function AppContent() {
  const { instance, accounts } = useMsal();
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [dataSource, setDataSource] = useState('demo');
  const [childSessionIds, setChildSessionIds] = useState([]);
  const [userRole, setUserRole] = useState(null); // 'editor', 'viewer', or 'unauthorized'
  const [permissions, setPermissions] = useState(derivePermissions([]));
  const [roleChecking, setRoleChecking] = useState(true);
  // Progress bar state - commented out for now, will revisit later
  // eslint-disable-next-line no-unused-vars
  const [loadProgress, setLoadProgress] = useState({ phase: '', percent: 0, message: '' });
  // eslint-disable-next-line no-unused-vars
  const progressIntervalRef = React.useRef(null);

  const isAuthenticated = accounts.length > 0;
  const account = accounts[0];

  // Check user's security group membership
  const checkUserRole = useCallback(async () => {
    if (!isAuthenticated) {
      setUserRole(null);
      setRoleChecking(false);
      return;
    }

    setRoleChecking(true);
    
    try {
      console.log('=== ROLE CHECK DEBUG ===');
      console.log('Checking role for account:', account?.username);
      
      // Get access token for Microsoft Graph
      const response = await instance.acquireTokenSilent({
        scopes: ["User.Read", "GroupMember.Read.All"],
        account: account
      });

      console.log('Graph token acquired successfully');

      // Fetch user's group memberships
      const groupResponse = await fetch('https://graph.microsoft.com/v1.0/me/memberOf', {
        headers: {
          'Authorization': `Bearer ${response.accessToken}`
        }
      });

      if (!groupResponse.ok) {
        const errorText = await groupResponse.text();
        console.error('Group fetch failed:', groupResponse.status, errorText);
        throw new Error(`Group fetch failed: ${groupResponse.status}`);
      }

      const groupData = await groupResponse.json();
      console.log('Raw group data:', groupData);
      
      const userGroupIds = groupData.value.map(group => group.id);
      console.log('User group IDs:', userGroupIds);
      console.log('Configured security groups:', securityGroups);

      const perms = derivePermissions(userGroupIds);
      setPermissions(perms);
      console.log('Derived permissions:', perms);

      // Calendar role (preserves existing 'editor' / 'viewer' / 'unauthorized'
      // contract used by Calendar.js, Administration.js, RegistrationReport.js).
      // IT Admins inherit calendarEditor; ScheduleManagers/Admins inherit viewer
      // so they can at least see the calendar in addition to the generator.
      if (perms.isCalendarEditor) {
        setUserRole('editor');
        console.log('User role: Editor');
      } else if (perms.isCalendarViewer) {
        setUserRole('viewer');
        console.log('User role: Viewer');
      } else {
        setUserRole('unauthorized');
        console.log('User role: Unauthorized');
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      console.error('Full error details:', error);
      setUserRole('unauthorized');
      setPermissions(derivePermissions([]));
    } finally {
      setRoleChecking(false);
      console.log('=== END ROLE CHECK DEBUG ===');
    }
  }, [isAuthenticated, account, instance]);

  // Authentication functions
  const handleLogin = async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await instance.logoutPopup();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Handle event data processing
  const handleEventData = useCallback(async (eventsData) => {
    // Get instructor assignments for events from cr38c_eventinstructors table
    const eventIds = eventsData.map(event => event.cr38c_eventid).filter(Boolean);
    
    if (eventIds.length > 0) {
      try {
        const eventInstructorMap = await authenticatedDataverseService.getEventInstructors(eventIds);
        
        // Get all contact GUIDs from event instructor assignments
        const allEventContactGuids = Object.values(eventInstructorMap)
          .flat()
          .map(instructor => instructor.contactGuid)
          .filter(Boolean);
          
        let eventContactMap = {};
        if (allEventContactGuids.length > 0) {
          eventContactMap = await authenticatedDataverseService.getContactNames(allEventContactGuids);
        }
        
        // Add instructor names and list to events
        eventsData = eventsData.map(event => {
          const eventId = event.cr38c_eventid;
          const instructorsForEvent = eventInstructorMap[eventId] || [];
          
          const instructorList = instructorsForEvent.map(instructor => ({
            name: eventContactMap[instructor.contactGuid] || 'Unknown Instructor',
            isPrimary: instructor.isPrimary,
            contactGuid: instructor.contactGuid
          }));
          
          instructorList.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
          const primaryInstructor = instructorList.find(i => i.isPrimary) || instructorList[0];
          
          return {
            ...event,
            instructors: instructorList,
            mi_instructor: primaryInstructor?.name || ''
          };
        });
      } catch (error) {
        console.error('Error processing event instructors:', error);
      }
    }
    
    // Merge events with existing sessions
    setSessions(prevSessions => [...prevSessions, ...eventsData]);
    console.log(`Loaded ${eventsData.length} events`);
  }, []);

  // Load sessions data
  const loadSessions = useCallback(async () => {
    if (!isAuthenticated) {
      setSessions([]);
      setDataSource('none');
      setConnectionStatus('unauthenticated');
      setLoading(false);
      setLoadProgress({ phase: '', percent: 0, message: '' });
      return;
    }

    setLoading(true);
    setError(null);
    setLoadProgress({ phase: 'priority', percent: 10, message: 'Loading sessions...' });
    
    try {
      // PHASE 1: Load priority sessions first (fastest possible)
      console.log('Loading priority sessions (-30 days to +90 days)...');
      setLoadProgress({ phase: 'priority', percent: 20, message: 'Fetching priority sessions...' });
      const prioritySessionResult = await authenticatedDataverseService.getTrainingSessions(true);
      
      if (prioritySessionResult.success) {
        setSessions(prioritySessionResult.data);
        setChildSessionIds(prioritySessionResult.childSessionIds || []);
        setConnectionStatus('connected');
        setDataSource('live');
        setLoading(false); // Show calendar immediately with priority data
        setLoadProgress({ phase: 'events', percent: 40, message: 'Loading events...' });
        
        // Track completion of parallel loads
        let eventsLoaded = false;
        let fullSessionsLoaded = false;
        
        const checkComplete = () => {
          if (eventsLoaded && fullSessionsLoaded) {
            setLoadProgress({ phase: '', percent: 0, message: '' });
          }
        };
        
        // Load events in parallel (non-blocking)
        authenticatedDataverseService.getEvents().then(eventResult => {
          if (eventResult.success) {
            handleEventData(eventResult.data);
          }
          eventsLoaded = true;
          checkComplete();
        }).catch(err => {
          console.error('Events load error:', err);
          eventsLoaded = true;
          checkComplete();
        });
        
        // PHASE 2: Load remaining data in background (full range)
        console.log('Loading remaining sessions in background...');
        setLoadProgress({ phase: 'background', percent: 50, message: 'Loading full session data...' });
        
        // Start animated progress while loading
        const startPercent = 50;
        const targetPercent = 95;
        let currentPercent = startPercent;
        
        progressIntervalRef.current = setInterval(() => {
          // Gradually increase, slowing down as we approach target
          const remaining = targetPercent - currentPercent;
          const increment = Math.max(0.5, remaining * 0.05);
          currentPercent = Math.min(targetPercent, currentPercent + increment);
          setLoadProgress(prev => ({ ...prev, percent: Math.round(currentPercent) }));
        }, 200);
        
        setTimeout(async () => {
          try {
            const fullResult = await authenticatedDataverseService.getTrainingSessions(false);
            
            // Stop the animated progress and hide bar immediately
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            setLoadProgress({ phase: '', percent: 0, message: '' });
            
            if (fullResult.success) {
              // Merge full session data while preserving any events already loaded
              setSessions(prevSessions => {
                const existingEvents = prevSessions.filter(s => s.isEvent);
                const newSessions = fullResult.data.filter(s => !s.isEvent);
                return [...newSessions, ...existingEvents];
              });
              setChildSessionIds(fullResult.childSessionIds || []);
              console.log(`Background load complete: ${fullResult.data.length} total sessions`);
            } else {
              console.error('Background load failed:', fullResult.error);
            }
            fullSessionsLoaded = true;
            checkComplete();
          } catch (err) {
            console.error('Background load error:', err);
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            setLoadProgress({ phase: '', percent: 0, message: '' });
            fullSessionsLoaded = true;
            checkComplete();
          }
        }, 100);
      } else {
        console.error('Dataverse sessions failed:', prioritySessionResult.error);
        setError(prioritySessionResult.error);
        setConnectionStatus('error');
        setDataSource('none');
        setSessions([]);
        setLoading(false);
        setLoadProgress({ phase: '', percent: 0, message: '' });
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
      setConnectionStatus('error');
      setDataSource('none');
      setSessions([]);
      setEvents([]);
      setLoadProgress({ phase: '', percent: 0, message: '' });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, handleEventData]);

  // Check user role when authentication changes
  useEffect(() => {
    checkUserRole();
  }, [checkUserRole]);

  // Load sessions when authentication changes and role is authorized
  useEffect(() => {
    if (userRole === 'editor' || userRole === 'viewer') {
      loadSessions();
    }
  }, [loadSessions, userRole]);

  const calendarPage = (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="/logo192.png" 
                alt="Maritime Institute Logo" 
                style={{ height: '100px', width: '100px', marginRight: '25px' }}
              />
              <h1 className="text-2xl font-bold text-gray-900">MI Global Calendar</h1>
              <span className="ml-3 text-xs font-medium text-gray-500">v{APP_VERSION}</span>
              <span className="ml-4 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                {dataSource === 'live' ? 'Live Data' : 'No Data'}
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <AuthenticatedTemplate>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-700">
                    Welcome, {account?.name}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </AuthenticatedTemplate>
              
              <UnauthenticatedTemplate>
                <button
                  onClick={handleLogin}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </button>
              </UnauthenticatedTemplate>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Unauthorized Access Message */}
        {isAuthenticated && userRole === 'unauthorized' && !roleChecking && (
          <div className="mb-6">
            <div className="p-6 rounded-lg bg-red-50 border border-red-200">
              <h3 className="font-medium text-red-800 text-lg mb-2">Access Denied</h3>
              <p className="text-red-600">
                You do not have the correct permissions to access this calendar. 
                Please contact your administrator to request access.
              </p>
            </div>
          </div>
        )}

        {/* Connection Status */}
        <div className="mb-6">
          <div className={`p-3 rounded-lg flex items-center justify-between ${
            connectionStatus === 'connected' ? 'bg-green-50 border border-green-200' :
            connectionStatus === 'error' ? 'bg-red-50 border border-red-200' :
            connectionStatus === 'unauthenticated' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            <span className={`text-sm font-medium ${
              connectionStatus === 'connected' ? 'text-green-800' :
              connectionStatus === 'error' ? 'text-red-800' :
              connectionStatus === 'unauthenticated' ? 'text-yellow-800' :
              'text-blue-800'
            }`}>
              {connectionStatus === 'connected' && 'Connected to Dataverse'}
              {connectionStatus === 'error' && `Error: ${error || 'Authentication failed'}`}
              {connectionStatus === 'unauthenticated' && 'Please Sign In'}
              {connectionStatus === 'checking' && 'Checking Connection...'}
            </span>
            
            {isAuthenticated && (
              <button
                onClick={loadSessions}
                disabled={loading}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
          </div>
          
          {/* Loading Progress Bar - Hidden for now
          {loadProgress.phase && (
            <div className="mt-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">{loadProgress.message}</span>
                <span className="text-xs text-gray-500">{loadProgress.percent}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ 
                    width: `${loadProgress.percent}%`,
                    backgroundColor: loadProgress.phase === 'complete' ? '#22c55e' : '#3b82f6'
                  }}
                />
              </div>
            </div>
          )}
          */}
        </div>

        {/* Calendar Component - Only show for authorized users */}
        {(userRole === 'editor' || userRole === 'viewer') && (
          <Calendar 
            sessions={sessions}
            events={events}
            loading={loading}
            dataSource={dataSource}
            account={account}
            isAuthenticated={isAuthenticated}
            childSessionIds={childSessionIds}
            userRole={userRole}
          />
        )}
      </main>
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={calendarPage} />
      <Route path="/administration" element={<Administration userRole={userRole} permissions={permissions} roleChecking={roleChecking} />} />
      <Route path="/schedule-generator" element={<ScheduleGenerator userRole={userRole} permissions={permissions} roleChecking={roleChecking} />} />
      <Route path="/registration-report" element={<RegistrationReport userRole={userRole} permissions={permissions} roleChecking={roleChecking} />} />
    </Routes>
  );
}

// Root App Component with MSAL Provider
function App() {
  return (
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <AppContent />
      </MsalProvider>
    </BrowserRouter>
  );
}

export default App;
